import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Point, LineString, Geometry } from 'ol/geom';
import { Circle, Fill, Icon, Stroke, Style, Text } from 'ol/style';
import { Feature } from 'ol';
import GeoJSON from 'ol/format/GeoJSON.js';

import { processRouteForMap, ProcessRouteOptions, routeLineStyles } from '../utils/map-utils';

import { VesselPlanningModel } from '../models/vessel-planning-model';
import { WeatherModel } from '../models/weather-model';
import { VoyageRoutingModel } from '../models/voyage-routing-model';
import { deepCopy, deepEqual } from '../utils/helpers';
import { RouteWeatherWorker } from '../utils/route-weather-worker';
import { VesselPositionModel } from '../models/vessel-position-model';
import { SuggestedRouteEvents } from '../../local_modules/voyage-suggested-routes/src/types';
import { SvgIcons } from '../../local_modules/voyage-svg-icons/src/voyage-svg-icons';
import { getWaypointDisplayStartIndex } from '../utils/route-utils';

const _PROJECTION = (<any>window)["appenv"].projection;

export interface ExecutionLayerColors
{
    defaultColor: string;
    waypointOutlineColor: string;
    waypointFillColor: string;
    textColor: string;
    passedColor: string;
    warningColor: string;
    avoidColor: string;
    parametricRollColor: string;
    heavyRollingColor: string;
    transverseAccelerationColor: string;
}

enum ELStyles
{
    WPHardStyle,
    WPNavStyle,
    WPPassedStyle,
    WPStoppageStyle
}

function isHiddenStoppageEndFeature(currentFeature: Feature<Geometry>, previousPointFeature: Feature<Geometry> | null): boolean {
    if (currentFeature.get("stoppage_end") !== true) {
        return false;
    }

    return previousPointFeature?.get("is_stoppage_point") === true;
}

export class ExecutionLayer extends EventTarget
{
    constructor(options: ExecutionLayerColors)
    {
        super();

        this._colors = {
            defaultColor: 'black',
            waypointOutlineColor: 'black',
            waypointFillColor: 'black',
            textColor: 'black',
            passedColor: 'black',
            warningColor: 'yellow',
            avoidColor: 'red',
            parametricRollColor: "#FE92E9",
            heavyRollingColor: "#8454D6",
            transverseAccelerationColor: "#BF6410",
        };
        this.setColors(options || {});

        this._plotExecutingPlan = this._plotExecutingPlan.bind(this);
    }

    get layer()
    {
        return this._layer;
    }

    async initialize()
    {
        this._planVectorSource = new VectorSource();
        this._layer = new VectorLayer({source: this._planVectorSource, visible: true});

        VesselPlanningModel!.addEventListener('executing-plan-changed', this._plotExecutingPlan);
        VesselPlanningModel!.addEventListener('vessel_characteristics_updated', this._plotExecutingPlan);
        WeatherModel!.addEventListener('weather-updated', this._plotExecutingPlan);
        VoyageRoutingModel!.addEventListener(SuggestedRouteEvents.WeatherLimitsChanged, this._plotExecutingPlan);
        VesselPositionModel!.addEventListener('position-updated', this._plotExecutingPlan);
    }

    clear()
    {
        if (this._planVectorSource)
        {
            this._planVectorSource.clear();
        }
    }

    setColors(colors: ExecutionLayerColors)
    {
        this._colors.defaultColor = colors.defaultColor || this._colors.defaultColor;
        this._colors.waypointOutlineColor = colors.waypointOutlineColor || this._colors.waypointOutlineColor;
        this._colors.waypointFillColor = colors.waypointFillColor || this._colors.waypointFillColor;
        this._colors.textColor = colors.textColor || this._colors.textColor;
        this._colors.passedColor = colors.passedColor || this._colors.passedColor;
        this._colors.warningColor = colors.warningColor || this._colors.warningColor;
        this._colors.avoidColor = colors.avoidColor || this._colors.avoidColor;
        this._colors.parametricRollColor = colors.parametricRollColor || this._colors.parametricRollColor;
        this._colors.heavyRollingColor = colors.heavyRollingColor || this._colors.heavyRollingColor;
        this._colors.transverseAccelerationColor = colors.transverseAccelerationColor || this._colors.transverseAccelerationColor;

        this._styles[ELStyles.WPHardStyle] = new Style({
            image: new Circle({
                radius: 4,
                fill: new Fill({color: this._colors.waypointFillColor.trim()}),
                stroke: new Stroke({
                  color: this._colors.waypointOutlineColor, width: 1
                })
            })
        });

        this._styles[ELStyles.WPNavStyle] = new Style({
            text: new Text({
                text: '+',
                font: 'bold 10px san-serif',
                fill: new Fill({color: this._colors.waypointFillColor.trim()}),
                stroke: new Stroke({color: this._colors.waypointOutlineColor, width: 1}),
                scale: 1.8,
                offsetY: 3
            })
        });

        this._styles[ELStyles.WPPassedStyle] = new Style({
            image: new Circle({
                radius: 4,
                fill: new Fill({color: this._colors.passedColor.trim()}),
                stroke: new Stroke({
                  color: this._colors.passedColor, width: 1
                })
            })
        });

        const stoppageSvg = new Image();
        stoppageSvg.src = 'data:image/svg+xml,' + encodeURIComponent(SvgIcons.minusCircle.strings[0]);

        this._styles[ELStyles.WPStoppageStyle] = new Style({
            image: new Icon({
                img: stoppageSvg,
                imgSize: [19, 19],
                anchor: [-5, 30],
                anchorYUnits: "pixels",
                anchorXUnits: "pixels",
            }),
            zIndex: 20
        });

        if (this._layer)
        {
            this._plotExecutingPlan();
        }
    }

    private async _plotExecutingPlan()
    {
        const plan = await VesselPlanningModel!.getExecutingPlan();

        if (!plan || plan.routes.length === 0 || plan.selectedRouteIndex < 0)
        {
            this._planVectorSource!.clear();
            return;
        }

        // Make a copy so we don't affect the real route
        let route = deepCopy(plan.routes[plan.selectedRouteIndex]);

        // Refresh weather information when new forecast is available or weather limits have been changed
        if (WeatherModel!.latestForecastVersion !== route.route_info!.calculationInput.forecast_versions[0] ||
            !deepEqual(route.route_info!.calculationInput.weatherLimits, VoyageRoutingModel!.getWeatherLimits()))
        {
            const weatherWorker = new RouteWeatherWorker(route, VoyageRoutingModel!.getWeatherLimits());
            await weatherWorker.processRoute();
        }

        // Add planIndex property
        for (let i = 0; i < route.route_geometry.length; i++)
        {
            route.route_geometry[i].properties.planIndex = i;
        }

        const geoJSON = {
            type: 'FeatureCollection',
            features: route.route_geometry
        };

        let features = (new GeoJSON()).readFeatures(geoJSON);
        const plotFeatures = [];

        const position = VesselPositionModel!.position;
        let nextWaypointIndex;
        if (position && position.latitude !== null && position.longitude !== null)
        {
            const result = await VoyageRoutingModel!.getNextWaypointIndex(75000, [position!.longitude, position!.latitude], route.route_geometry);
            if (result.result !== "failure")
            {
                nextWaypointIndex = result.nextWPIndex;
            }
        }

        let currentPos: [number, number]|undefined;
        if (VesselPositionModel!.position
            && VesselPositionModel!.position!.longitude
            && VesselPositionModel!.position!.latitude)
        {
            currentPos = [
                VesselPositionModel!.position!.longitude,
                VesselPositionModel!.position!.latitude
            ]
        }

        const processRouteOptions: ProcessRouteOptions  = {
            features: features,
            currentPosition: currentPos,
            nextWaypointIndex: nextWaypointIndex,
            addWarnings: true
        }
        features = processRouteForMap(processRouteOptions);

        const vesselCharacteristics = await VesselPlanningModel!.getVesselCharacteristics?.();
        let PPIndex = getWaypointDisplayStartIndex(vesselCharacteristics?.ecdisWPStartIndex);
        let previousPointFeature: Feature<Geometry> | null = null;
        for (let feature of features)
        {
            feature.set("layer", "ExecutionLayer");
            if (feature.getGeometry() instanceof LineString)
            {
                const geometry = <LineString>feature.getGeometry();
                const coords = geometry.getCoordinates();

                // check if this lineString crosses the IDL
                let hasIDLCrossing = false;
                for (let i = 1; i < coords.length; i++)
                {
                    const prevLon = coords[i - 1][0];
                    const currLon = coords[i][0];
                    const lonDiff = currLon - prevLon;

                    if (Math.abs(lonDiff) > 180)
                    {
                        hasIDLCrossing = true;
                        break;
                    }
                }

                if (!hasIDLCrossing)
                {
                    // no IDL crossing - process normally
                    feature.getGeometry()!.transform('EPSG:4326', _PROJECTION);
                    feature.setStyle((feature, resolution) => routeLineStyles(<Feature<LineString>>feature, resolution, this._colors));
                    plotFeatures.push(feature);
                }
                else
                {
                    // splitting lineString at IDL crossings
                    const normalizedCoords: number[][] = [];
                    for (let i = 0; i < coords.length; i++)
                    {
                        if (i === 0)
                        {
                            normalizedCoords.push(coords[i]);
                        }
                        else
                        {
                            const prevLon = coords[i - 1][0];
                            const currLon = coords[i][0];
                            const lonDiff = currLon - prevLon;

                            if (Math.abs(lonDiff) > 180)
                            {
                                // creating segment before crossing
                                if (normalizedCoords.length > 1)
                                {
                                    const newLineString = new LineString(normalizedCoords);
                                    newLineString.transform('EPSG:4326', _PROJECTION);
                                    const newFeature = new Feature({
                                        geometry: newLineString,
                                        layer: "ExecutionLayer",
                                        ...feature.getProperties()
                                    });
                                    newFeature.setStyle((feature, resolution) => routeLineStyles(<Feature<LineString>>feature, resolution, this._colors));
                                    plotFeatures.push(newFeature);
                                }

                                // starting new segment after crossing
                                normalizedCoords.length = 0;
                                normalizedCoords.push(coords[i]);
                            }
                            else
                            {
                                normalizedCoords.push(coords[i]);
                            }
                        }
                    }

                    // adding remaining segment
                    if (normalizedCoords.length > 1)
                    {
                        const newLineString = new LineString(normalizedCoords);
                        newLineString.transform('EPSG:4326', _PROJECTION);
                        const newFeature = new Feature({
                            geometry: newLineString,
                            layer: "ExecutionLayer",
                            ...feature.getProperties()
                        });
                        newFeature.setStyle((feature, resolution) => routeLineStyles(<Feature<LineString>>feature, resolution, this._colors));
                        plotFeatures.push(newFeature);
                    }
                }
            }
            else if (feature.getGeometry() instanceof Point)
            {
                if (isHiddenStoppageEndFeature(feature, previousPointFeature))
                {
                    previousPointFeature = feature;
                    continue;
                }

                // Add passage plan index to hard points
                if (feature.get("type") === "Hard")
                {
                    PPIndex++;
                    feature.set("PPIndex", PPIndex);
                }
                feature.getGeometry()!.transform('EPSG:4326', _PROJECTION);
                feature.setStyle(this._getWPStyle(feature));
                plotFeatures.push(feature);
                previousPointFeature = feature;
            }
        }

        this._planVectorSource!.clear();
        this._planVectorSource!.addFeatures(plotFeatures);
    }

    private _getWPStyle(feature: Feature<Geometry>): Style[]
    {
        const styles: Style[] = [];
        const isStoppage = feature.get("is_stoppage_point") === true;

        if (feature.get("isPassed"))
        {
            styles.push(<Style>this._styles[ELStyles.WPPassedStyle]);
            if (isStoppage)
            {
                styles.push(<Style>this._styles[ELStyles.WPStoppageStyle]);
            }
        }
        else
        {
            styles.push(<Style>(feature.get("type") === "Hard" ? isStoppage ? this._styles[ELStyles.WPStoppageStyle] : this._styles[ELStyles.WPHardStyle] : this._styles[ELStyles.WPNavStyle]));
        }

        if (feature.get("PPIndex") !== undefined && feature.get("PPIndex") !== null)
        {
            styles.push(new Style({
                text: new Text({
                    font: "12px MaerskText",
                    text: `${feature.get("PPIndex")}`,
                    fill: new Fill({
                        color: this._colors.textColor
                    }),
                    offsetX: -5,
                    textAlign: "right",
                    textBaseline: "alphabetic"
                })
            }));
        }

        return styles;
    }

    private _planVectorSource: VectorSource<Geometry>|null = null;
    private _layer: VectorLayer<VectorSource<Geometry>>|null = null;
    private _colors: ExecutionLayerColors;
    private _styles: (Style|Style[])[] = [];
};
